import CryptoInputDisplay from '@components/CryptoInputDisplay/CryptoInputDisplay';
import CryptoInputSwitcher from '@components/CryptoInputSwitcher/CryptoInputSwitcher';
import CryptoPreviewBottomSheet from '@components/CryptoPreviewBottomSheet/CryptoPreviewBottomSheet';
import Header from '@components/Header/Header';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import Snackbar from '@components/Snackbar/Snackbar';
import useCryptoDepositAddress from '@hooks/useCryptoDepositAddress';
import useExchangeRate, { ExchangeRateResponse } from '@hooks/useExchangeRate';
import { useNumericKeyboardWithSymbol } from '@hooks/useNumericKeyboardWithSymbol';
import { convertDecimalFormat, roundAmount, toCurrencyFormat, toCurrencyFormatWithDecimal } from '@lib/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  PageContainer,
  ContentContainer,
  TopSection,
  InputSection,
  MinimumAmountSection,
  MinimumAmountText,
  BoldText,
  BottomSection,
  Separator,
  KeyboardSection,
  KeyboardContainer,
  ButtonContainer
} from './TopUpCryptoPreview.styles';

const MINIMUM_JIDR_AMOUNT = 100_000

const TopUpCryptoPreview: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const iconUrl = location.state?.iconUrl;
  const assetId = location.state?.id;
  const ticker = location.state?.ticker;
  const decimal = location.state?.decimal;
  const cryptoName = location.state?.cryptoName
  const network = location.state?.network

  const navigate = useNavigate();
  const [switchTo, setSwitchTo] = useState<'JIDR' | string>(ticker ?? 'JIDR');
  const [inputAssetIconUrl, setInputAssetIconUrl] = useState<string | undefined>(undefined);
  const [inputAssetTicker, setInputAssetTicker] = useState<string | undefined>(undefined);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false)

  const [rate, setRate] = useState<ExchangeRateResponse>()

  const { amount, handleKeyboardPress, setAmount } = useNumericKeyboardWithSymbol('0')

  const {
    getExchangeRate,
    isLoading: isRateLoading
  } = useExchangeRate()

  const {
    getDepositAddress,
  } = useCryptoDepositAddress()

  const handleRefreshRate = async () => {
    try {
      setIsError(false);
      const mode = switchTo === 'JIDR' ? 'variable-jidr' : 'fixed-jidr'
      const amountInDecimal = mode === 'variable-jidr' ? convertDecimalFormat(amount) : amount;
      const rateResponse = await getExchangeRate(assetId, amountInDecimal, mode)
      setRate(rateResponse);
      if (Number(rateResponse.total) < MINIMUM_JIDR_AMOUNT) {
        setIsError(true)
      }
    } catch (error) {
      setRate(undefined);
      setIsError(true);
    }
  }

  const handlePreviewClick = async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const mode = switchTo === 'JIDR' ? 'variable-jidr' : 'fixed-jidr'
      const amountInDecimal = mode === 'variable-jidr' ? convertDecimalFormat(amount) : amount;
      const rateResponse = await getExchangeRate(assetId, amountInDecimal, mode)
      setRate(rateResponse);
      if (Number(rateResponse.total) < MINIMUM_JIDR_AMOUNT) {
        setIsError(true)
      }
    } catch (error) {
      setRate(undefined);
      setIsError(true);
    } finally {
      setIsLoading(false);
      setIsBottomSheetOpen(true);
    }
  }

  const handleGetDepositAddress = async () => {
    try {
      setIsLoading(true)
      setSnackbarVisible(false)
      const depositAddress = await getDepositAddress(assetId)
      setIsBottomSheetOpen(false);
      navigate('/top-up-crypto-deposit-address', {
        state: {
          id: assetId,
          cryptoName,
          network,
          iconUrl,
          depositAddress,
          rate,
          ticker,
          decimal,
          amount: rate?.size
        }
      })
    } catch (error) {
      setSnackbarVisible(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageContainer>
      <Header
        title={t('topUpCrypto.preview.header')}
        showBackButton
        onBackClick={() => navigate('/top-up-crypto-select-asset')}
      />

      <Snackbar
        isVisible={snackbarVisible}
        textAlign='center'
        onClose={() => setSnackbarVisible(false)}
        message={t('errors.1007.description')}
        duration={5000}
        width='90%'
      />

      <LoadingModal
        visible={isLoading}
      />

      <ContentContainer>

        <TopSection>
          <InputSection>
            <CryptoInputDisplay
              iconUrl={inputAssetIconUrl}
              ticker={inputAssetTicker?.toLocaleUpperCase()}
              amount={switchTo === 'JIDR' ? toCurrencyFormatWithDecimal(amount, decimal, 'none') : toCurrencyFormat(Number(amount))}
            />
            <CryptoInputSwitcher
              switchTo={(switchTo || '').toUpperCase()}
              onSwitch={() => {
                setSwitchTo(switchTo === 'JIDR' ? ticker ?? 'JIDR' : 'JIDR')
                setInputAssetIconUrl(switchTo === 'JIDR' ? undefined : iconUrl);
                setInputAssetTicker(switchTo === 'JIDR' ? undefined : ticker?.toUpperCase());
                setAmount('0');
              }}
            />
          </InputSection>

          <MinimumAmountSection>
            <MinimumAmountText>
              {t('topUpCrypto.preview.minimumLimitStylized.minimumTopUpAmountIs')}
              <BoldText>{` JIDR ${toCurrencyFormat(MINIMUM_JIDR_AMOUNT)} `}</BoldText>
              {t('topUpCrypto.preview.minimumLimitStylized.pleaseMakeSureYouHaveValidAmount')}
            </MinimumAmountText>
          </MinimumAmountSection>

        </TopSection>

        <BottomSection>
          <Separator />

          <KeyboardSection>
            <KeyboardContainer>
              <NumericKeyboard
                onChange={handleKeyboardPress}
                emptySpaceButton={switchTo === 'JIDR' ? 'comma' : 'thousand'}
              />
            </KeyboardContainer>
            <ButtonContainer>
              <PrimaryButton onClick={handlePreviewClick} >
                {t('topUpCrypto.preview.previewButton')}
              </PrimaryButton>
            </ButtonContainer>
          </KeyboardSection>

        </BottomSection>

      </ContentContainer>

      <CryptoPreviewBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        fromAmount={toCurrencyFormatWithDecimal(roundAmount(Number(rate?.size || '0'), 'up', decimal), decimal)}
        fromAsset={ticker?.toUpperCase()}
        fromAssetIconUrl={iconUrl}
        toAmount={toCurrencyFormat(Number(rate?.total || 0))}
        rate={toCurrencyFormat(Number(rate?.ex_rate || 0))}
        isButtonDisabled={isError}
        isError={isError}
        onGetDepositAddress={handleGetDepositAddress}
        validTime={rate?.valid_time}
        onRefreshRate={handleRefreshRate}
        isRefreshing={isRateLoading}
      />

    </PageContainer>
  )
}

export default TopUpCryptoPreview;